import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import { useAuth } from './AuthContext';

const ContentContext = createContext();

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const { user, profile } = useAuth();

  // Match ProtectedRoute / AuthContext: token refresh can clear merged role on user briefly
  const isOwner = (user?.role ?? profile?.role) === 'owner';

  // Fetch content from Supabase
  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('website_content')
        .select('*');

      if (error) throw error;

      // Transform data into a more usable structure
      const contentObj = {};
      data.forEach(item => {
        if (!contentObj[item.section]) {
          contentObj[item.section] = {};
        }
        contentObj[item.section][item.key] = item.content;
      });

      setContent(contentObj);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update content in Supabase
  const updateContent = async (section, key, newContent) => {
    try {
      const { error } = await supabase
        .from('website_content')
        .update({ content: newContent })
        .match({ section, key });

      if (error) throw error;

      // Update local state
      setContent(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [key]: newContent
        }
      }));

      return { success: true };
    } catch (error) {
      console.error('Error updating content:', error);
      return { success: false, error };
    }
  };

  // Upload an image to Supabase storage
  const uploadImage = async (file, path) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${path}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('website-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('website-images')
        .getPublicUrl(filePath);

      return { success: true, url: data.publicUrl };
    } catch (error) {
      console.error('Error uploading image:', error);
      return { success: false, error };
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    if (isOwner) {
      setIsEditMode(prev => !prev);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const value = {
    content,
    loading,
    isEditMode,
    isOwner,
    toggleEditMode,
    updateContent,
    uploadImage,
    refreshContent: fetchContent
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => useContext(ContentContext);